import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Input, Table, Typography } from 'antd';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { createSearchParams, Link, Outlet, useMatch, useSearchParams } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import Filter from '../../components/Filter';
import SearchParam from '../../components/SearchParam';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { UserCollection } from '../../types';

const Users = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const match = useMatch(':tenant/settings/cs-staff/users/*');

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();

  const config = useAPIQuery('/configs/tenant-configurations/');

  useEffect(() => {
    if (match && config.data?.t001_enableCentralizedAuthIntegration) {
      navigate(
        {
          pathname: '/:tenant/settings/accessManagement/users/',
          params: { tenant },
          search: searchParams,
        },
        { replace: true }
      );
    }
  }, [config.data?.t001_enableCentralizedAuthIntegration, match, navigate, searchParams, tenant]);

  const users = useAPIQuery(
    [
      '/users/',
      {
        params: {
          page: parseInt(searchParams.get('page') || '1', 10),
          page_size: parseInt(searchParams.get('pageSize') || '10', 10),
          ...(searchParams.get('search') ? { search: searchParams.get('search') as string } : {}),
          is_active: match ? undefined : true,
        },
      },
    ],
    {
      keepPreviousData: true,
    }
  );

  const groupsPages = useInfiniteAPIQuery(['/groups/']);

  const groups = usePrefetchPages(groupsPages, (data) => data.results);

  return (
    <>
      <Helmet>
        <title>Usuários | Configurações | Recrutamento e Seleção</title>
      </Helmet>
      <Outlet />
      <Filter>
        <Col flex="auto">
          <SearchParam name="search" onSetSearchParams={{ page: '1' }}>
            <Input.Search allowClear style={{ marginBottom: 10 }} />
          </SearchParam>
        </Col>
        {match && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() =>
                navigate({
                  pathname: '/:tenant/settings/cs-staff/users/create/',
                  params: { tenant },
                })
              }
            >
              Novo usuário
            </Button>
          </Col>
        )}
      </Filter>
      <Table
        rowKey="id"
        dataSource={users.data?.results}
        loading={users.isFetching || groupsPages.isFetching}
        pagination={{
          showTotal: (total) => {
            return `Total: ${total} usuários`;
          },
          total: users.data?.count,
          onChange: (page, pageSize) => {
            searchParams.set('page', page.toString());
            searchParams.set('pageSize', pageSize.toString());
            setSearchParams(searchParams);
          },
          current: parseInt(searchParams.get('page') || '1', 10),
          pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
        }}
      >
        <Table.Column
          title="Usuário"
          render={(record: UserCollection) => (
            <Link
              to={{
                pathname: `${record.id}/change`,
                search: createSearchParams(searchParams).toString(),
              }}
            >
              {record.username}
            </Link>
          )}
        />
        <Table.Column
          title="Nome"
          render={(record: UserCollection) => <Typography.Text>{record.full_name}</Typography.Text>}
        />
        <Table.Column
          title="Perfil"
          render={(record: UserCollection) =>
            record.groups.length > 0 && (
              <Typography.Text>
                {groups.find((group) => group.id === record.groups[0])?.name}
              </Typography.Text>
            )
          }
        />
        {match && (
          <Table.Column
            title="Status"
            render={(record: UserCollection) => {
              if (record.is_active) {
                return <Typography.Text>Ativo</Typography.Text>;
              }
              return <Typography.Text>Desativado</Typography.Text>;
            }}
          />
        )}
      </Table>
    </>
  );
};

export default withRouteChange()(Users);
